/* ─────────────────────────────────────────────────────────────
   Default Registry Setup — Registers all built-in field renderers
   with their corresponding display (review) components.
   Call this once at app initialization.
   ───────────────────────────────────────────────────────────── */

import { registerRenderer } from './registry';
import { registerCustomRule } from '../validation/adapter';
import { TextFieldRenderer } from './renderers/TextField';
import { NumberFieldRenderer } from './renderers/NumberField';
import { DateFieldRenderer } from './renderers/DateField';
import { SelectFieldRenderer } from './renderers/SelectField';
import { CurrencySelectRenderer } from './renderers/CurrencySelect';
import { TextAreaRenderer } from './renderers/Textarea';
import { CheckboxRenderer } from './renderers/Checkbox';
import { ListBuilderRenderer } from './renderers/ListBuilder';
import { MonthDayFieldRenderer, MonthDayDisplay } from './renderers/MonthDayField';
import { FyeSelectRenderer, FyeSelectDisplay } from './renderers/FyeSelect';
import { OnboardUnderField, OnboardUnderDisplay } from './renderers/OnboardUnderField';
import { OnboardUnderOrgField, OnboardUnderOrgDisplay } from './renderers/OnboardUnderOrgField';
import { PhoneFieldRenderer, PhoneDisplay } from './renderers/PhoneField';
import { PartyPickerField, PartyPickerDisplay } from './renderers/PartyPickerField';
import { EngagementJurisdictionField, EngagementJurisdictionDisplay } from './renderers/EngagementJurisdictionField';
import { DependentSelectField, DependentSelectDisplay } from './renderers/DependentSelectField';
import { NicPickerField, NicPickerDisplay } from './renderers/NicPickerField';
import { MoneyFieldRenderer, MoneyDisplay } from './renderers/MoneyField';
import { moneyFormatError } from './renderers/moneyFormat';
import { ComputedFieldRenderer, ComputedDisplay } from './renderers/ComputedField';
import { CatalogueSearchSelectRenderer, CatalogueSearchSelectDisplay } from './renderers/CatalogueSearchSelectField';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  TextDisplay,
  NumberDisplay,
  DateDisplay,
  SelectDisplay,
  CurrencySelectDisplay,
  TextAreaDisplay,
  CheckboxDisplay,
  ListBuilderDisplay,
} from './renderers/Displays';

let initialized = false;

export function setupDefaultRegistry() {
  if (initialized) return;

  registerRenderer('textField', TextFieldRenderer, TextDisplay);
  registerRenderer('numberField', NumberFieldRenderer, NumberDisplay);
  registerRenderer('date', DateFieldRenderer, DateDisplay);
  registerRenderer('select', SelectFieldRenderer, SelectDisplay);
  registerRenderer('currencySelect', CurrencySelectRenderer, CurrencySelectDisplay);
  registerRenderer('textarea', TextAreaRenderer, TextAreaDisplay);
  registerRenderer('checkbox', CheckboxRenderer, CheckboxDisplay);
  registerRenderer('listBuilder', ListBuilderRenderer, ListBuilderDisplay);
  registerRenderer('monthDay', MonthDayFieldRenderer, MonthDayDisplay);
  registerRenderer('fyeSelect', FyeSelectRenderer, FyeSelectDisplay);
  // Custom: client-onboarding "onboard under" (SA: Self / Agency) selector.
  registerRenderer('onboardUnder', OnboardUnderField, OnboardUnderDisplay);
  // Custom: worker-onboarding "engaging org" (SA: agency or client; AGENCY: self + clients).
  registerRenderer('onboardUnderOrg', OnboardUnderOrgField, OnboardUnderOrgDisplay);
  // Phone: country-code selector + validated number (stores E.164).
  registerRenderer('phone', PhoneFieldRenderer, PhoneDisplay);
  // Engagement-create: client director (party) picker.
  registerRenderer('partyPicker', PartyPickerField, PartyPickerDisplay);
  // Engagement-create: jurisdiction (country + dependent entity type).
  registerRenderer('engagementJurisdiction', EngagementJurisdictionField, EngagementJurisdictionDisplay);
  // General-purpose cascading select (options depend on a sibling field's value).
  registerRenderer('dependentSelect', DependentSelectField, DependentSelectDisplay);
  // ENT-400: MCA NIC sub-class — suggests from a derived catalogue, still accepts free text.
  registerRenderer('nicPicker', NicPickerField, NicPickerDisplay);

  // ENT-565 FR-67: money (a decimal string), computed (server value, read-only), catalogue search select.
  registerRenderer('money', MoneyFieldRenderer, MoneyDisplay);
  registerRenderer('computed', ComputedFieldRenderer, ComputedDisplay);
  registerRenderer('catalogueSearchSelect', CatalogueSearchSelectRenderer, CatalogueSearchSelectDisplay);

  // `money` must be a plain decimal (digits, at most two decimals). The resolver adds this rule to
  // every `money` field, so a config never has to remember it. Empty is allowed (use `required`).
  registerCustomRule('moneyDecimal', (value) => moneyFormatError(value));

  // Validity check for the `phone` field — empty is allowed (use `required` to forbid).
  registerCustomRule('phone', (value) => {
    const v = (value as string) ?? '';
    if (!v) return undefined;
    return isValidPhoneNumber(v) ? undefined : 'Enter a valid phone number for the selected country.';
  });

  // Repeating group (listBuilder) row count. Empty lists are caught by `required` first.
  registerCustomRule('minItems', (value, args) => {
    const min = Number(args?.min ?? 0);
    const count = Array.isArray(value) ? value.length : 0;
    if (count >= min) return undefined;
    return (args?.message as string | undefined) ?? `At least ${min} required`;
  });

  // Cross-field validation rules
  registerCustomRule('dateNotBefore', (value, args, getFieldValue) => {
    if (!value || !args?.field) return undefined;
    const otherValue = getFieldValue?.(args.field as string);
    if (!otherValue) return undefined;
    if (new Date(value as string) < new Date(otherValue as string)) {
      return (args.message as string) ?? 'Must be on or after the start date';
    }
    return undefined;
  });

  registerCustomRule('dateNotAfter', (value, args, getFieldValue) => {
    if (!value || !args?.field) return undefined;
    const otherValue = getFieldValue?.(args.field as string);
    if (!otherValue) return undefined;
    if (new Date(value as string) > new Date(otherValue as string)) {
      return (args.message as string) ?? 'Must be on or before the end date';
    }
    return undefined;
  });

  initialized = true;
}
